






import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { OsType, getOSType, isCameraUsable } from '@/util/device';
import { sendLog } from '@/api/client-logs';
import { CameraDevice, FacingMode } from '@/store/modules/camera';

/// iOS, Androidどっちでも使えるCameraコンポーネント

export enum IdealSize {
  Size320x240 = '320x240',
  Size640x480 = '640x480',
  Size800x600 = '800x600',
  Size1280x720 = '1280x720',
  Size1600x960 = '1600x960',
  Size1920x1080 = '1920x1080',
  Size3840x2160 = '3840x2160',
  Max = 'max'
}

interface MediaSettingRange {
  min: number;
  max: number;
  step: number;
}

interface Point2D {
  x: number;
  y: number;
}

@Component
export default class CameraPreview extends Vue {
  @Prop({ default: FacingMode.Environment })
  facingMode!: FacingMode;

  @Prop({ default: IdealSize.Max })
  idealSize!: IdealSize;

  @Prop({ type: Boolean, default: false })
  isAlreadyPermitted!: Boolean;

  // Vue クラスのプロパティをオーバーライド
  // (HTML***Element 型などを利用するため。必須ではない)
  $refs!: {
    video: HTMLVideoElement;
  };

  public video!: HTMLVideoElement;

  private facingModeInternal!: FacingMode;

  private currentTrack!: MediaStreamTrack;
  private currentDeviceId: string = '';
  private currentTimerId: number = -1;

  private get selectedDevice(): CameraDevice | undefined {
    return this.$store.state.camera.videoDevices[this.$store.state.camera.selectedDeviceIndex[this.facingMode]];
  }

  private get isAndroid(): boolean {
    return this.$store.getters['camera/isAndroid'];
  }

  private get isRefreshRequesting(): boolean {
    return this.$store.state.camera.isRefreshRequesting;
  }

  data(): { facingModeInternal: FacingMode } {
    return {
      facingModeInternal: this.facingMode
    };
  }

  async mounted() {
    // Check constraints support
    if (!isCameraUsable()) throw new Error('This device is not supported.');

    this.video = this.$refs.video;
    this.video.addEventListener('loadedmetadata', this.onLoadedMetaData);
    const osType = getOSType();

    if (osType === OsType.Android) {
      // 未許可の状態で来た(初めてページを訪れた等の)場合にfalse
      if (this.isAlreadyPermitted === false) {
        // 権限を持たない場合はデバイス一覧のラベルを取得できないので、権限を請求するためにカメラを開いて閉じる
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          stream.getTracks().forEach(track => track.stop());
        } catch (error) {}
      }
    }

    const devices = await this.getVideoDevices();
    this.$store.commit('camera/setVideoDevices', devices);

    if (osType === OsType.PC) {
      const stream: MediaStream | null = await this.getStreamForPC();
      if (stream === null) return;
      this.setStream(stream);
    } else {
      const constraints: MediaStreamConstraints = this.createConstraints();
      await this.startCamera(constraints);
    }

    // スリープ等の監視
    document.addEventListener('visibilitychange', this.visibilitychangeHandle);

    // 初回のselectedDeviceは上で設定されるのでmount後にwatchを始める
    this.$watch('selectedDevice', this.startSelectedCamera);
  }

  private async startSelectedCamera() {
    this.$store.commit('camera/setIsCameraChanging', true);
    const constraints: MediaStreamConstraints = this.createConstraints();
    try {
      await this.startCamera(constraints);
    } catch {
      if (this.isAndroid && this.selectedDevice) {
        this.$store.commit('camera/setUnavailableCamera', this.selectedDevice.deviceId);
        this.$store.dispatch('camera/useNextCameraMode', this.facingMode);
      }
    }
    this.$store.commit('camera/setIsCameraChanging', false);
  }

  @Watch('isRefreshRequesting')
  private async handleRefreshRequest() {
    if (this.isRefreshRequesting === false) return;

    this.$store.commit('camera/setIsRefreshRequesting', false);
    await this.startSelectedCamera();
  }

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.visibilitychangeHandle);

    this.video.removeEventListener('loadedmetadata', this.onLoadedMetaData);
    this.stopCamera();
  }

  // Public methods

  public pauseVideo() {
    this.video.pause();
  }

  public replayVideo() {
    this.video.play();
  }

  // 使っていない機能
  public async toggleCamera(): Promise<void> {
    if (getOSType() === OsType.Android) {
      // Androidでは、全部のカメラを取得して、順番に切り替える。
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        throw new Error('enumerateDevices() not supported.');
      }
      if (!this.currentTrack) {
        return; // Camera has not started
      }
      if (this.currentDeviceId === '') {
        return; //
      }
      let devices = await navigator.mediaDevices.enumerateDevices();
      devices = devices.filter(device => device.kind === 'videoinput');
      if (devices.length === 1) {
        return;
      }
      const index = devices.findIndex(device => device.deviceId === this.currentDeviceId);
      const nextIndex = index === devices.length - 1 ? 0 : index + 1;
      const nextDevice = devices[nextIndex];
      const constraints: MediaStreamConstraints = {
        audio: false,
        video: { deviceId: { exact: nextDevice.deviceId } }
      };
      await this.startCamera(constraints);
    } else {
      // iOSでは初回のthis.currentDeviceIdが取得できないので、単にfacingModeを切り替える。
      this.facingModeInternal = `${this.facingModeInternal}` === 'user' ? FacingMode.Environment : FacingMode.User;
      const constraints: MediaStreamConstraints = this.createConstraints();
      await this.startCamera(constraints);
    }
  }

  // Public getter / setter

  // 使っていない機能
  public torch(value: boolean) {
    this.applyAdvancedConstraints('torch', value);
  }

  // 使っていない機能
  public colorTemperature(value: number) {
    this.applyAdvancedConstraintsRange('colorTemperature', value);
  }

  // 使っていない機能
  public exposureMode(value: string) {
    this.applyAdvancedConstraints('exposureMode', value);
  }

  // 使っていない機能
  public exposureCompensation(value: number) {
    this.applyAdvancedConstraintsRange('exposureCompensation', value);
  }

  // 使っていない機能
  public iso(value: number) {
    this.applyAdvancedConstraintsRange('iso', value);
  }

  // 使っていない機能
  public focusMode(value: string) {
    this.applyAdvancedConstraints('focusMode', value);
  }

  // 使っていない機能
  public whiteBalanceMode(value: string) {
    this.applyAdvancedConstraints('whiteBalanceMode', value);
  }

  // 使っていない機能
  public zoom(value: number) {
    this.applyAdvancedConstraintsRange('zoom', value);
  }

  // 使っていない機能
  public brightness(value: number) {
    this.applyAdvancedConstraintsRange('brightness', value);
  }

  // 使っていない機能
  public contrast(value: number) {
    this.applyAdvancedConstraintsRange('contrast', value);
  }

  // 使っていない機能
  public saturation(value: number) {
    this.applyAdvancedConstraintsRange('saturation', value);
  }

  // 使っていない機能
  public sharpness(value: number) {
    this.applyAdvancedConstraintsRange('sharpness', value);
  }

  // 使っていない機能
  public focusDistance(value: number) {
    this.applyAdvancedConstraintsRange('focusDistance', value);
  }

  // 使っていない機能
  public pointsOfInterest(x: number, y: number) {
    const points: [Point2D] = [
      {
        x,
        y
      }
    ];
    this.applyAdvancedConstraints('pointsOfInterest', points);
  }

  private async getVideoDevices(): Promise<MediaDeviceInfo[]> {
    if (this.isAndroid && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      let devices = await navigator.mediaDevices.enumerateDevices();
      devices = devices.filter(device => device.kind === 'videoinput');

      // デバイスのラベルが一定のフォーマットにしたがっていると仮定して、その一覧を取得する
      if (this.facingModeInternal === 'user') {
        devices = devices.filter(device => device.label.match(/^camera\d* \d*, facing front$/));
      } else {
        devices = devices.filter(device => device.label.match(/^camera\d* \d*, facing back$/));
      }

      // 最も若い番号のカメラが普通のカメラと信じて選択する
      if (devices.length >= 1) {
        devices.sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });

        // まだカメラデバイスが選択されていない場合のみセットする
        if (this.selectedDevice === null) {
          this.$store.commit('camera/setCameraModeIndex', {
            mode: this.facingMode,
            index: 0
          });
        }
      }

      return devices;
    }

    return [];
  }

  private createConstraints(): MediaStreamConstraints {
    if (this.selectedDevice?.deviceId) {
      return {
        audio: false,
        video: {
          deviceId: { exact: this.selectedDevice?.deviceId },
          width: this.sizeConstraints.width!
        }
      };
    }

    return {
      audio: false,
      video: {
        facingMode: this.facingModeInternal,
        width: this.sizeConstraints.width!
      }
    };
  }

  private async getStreamForPC(): Promise<MediaStream | null> {
    try {
      // とりあえずストリームの取得を試みる
      return await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: this.facingModeInternal,
          width: this.sizeConstraints.width
        }
      });
    } catch (error) {
      // ストリームの取得に失敗
      if (error instanceof Error && error.name === 'NotAllowedError' && /[P|p]ermission/.test(error.message)) {
        // パーミッションのエラー
        sendLog({ level: 'INFO', kind: 'camera_permission', message: error.message });
        this.permissionDenied(error);
        return null;
      } else if (!navigator.mediaDevices.enumerateDevices) {
        // これから使いたい関数を使うことができないエラー
        const message: string = error instanceof Error ? error.message : '';
        sendLog({ level: 'INFO', kind: 'camera_no_required_function', message });
        this.unsupportedDevice(error);
        return null;
      } else {
        // デバイス一覧を取得
        const devicesAll: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices();
        const devicesVideo: MediaDeviceInfo[] = devicesAll.filter(device => device.kind === 'videoinput');
        // 起動に成功するかデバイスが尽きるまでストリームの取得を試みる
        for (let i = 0; i < devicesVideo.length; i++) {
          const stream: MediaStream | null = await navigator.mediaDevices
            .getUserMedia({
              audio: false,
              video: {
                deviceId: { exact: devicesVideo[i].deviceId },
                facingMode: this.facingModeInternal,
                width: this.sizeConstraints.width
              }
            })
            .catch(() => null);
          if (stream !== null) return stream;
        }
        // 正常に使えるデバイスが存在しないエラー
        const message: string = error instanceof Error ? error.message : '';
        sendLog({ level: 'INFO', kind: 'camera_unsupported', message });
        this.unsupportedDevice(error);
        return null;
      }
    }
  }

  // Private methods
  private async startCamera(constraints: MediaStreamConstraints) {
    // Stop before camere if exists
    this.stopCamera();

    // Try to gat stream
    const stream = (await navigator.mediaDevices.getUserMedia(constraints).catch(err => {
      const message: string = (err || {}).message || '';
      sendLog({ level: 'INFO', kind: 'camera_starting_failed', message });
      this.permissionDenied(err);
      this.error(err);
    })) as MediaStream;
    if (!stream) {
      return;
    }

    this.setStream(stream);
  }

  // ストリームの設定
  private setStream(stream: MediaStream): void {
    this.currentTrack = stream.getVideoTracks()[0];

    // FireFox は MediaStreamTrack::getCapabilities() を実装していない (おそらくプライバシーの問題)
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1179084
    if (this.currentTrack.getCapabilities) {
      const capabilities: MediaTrackCapabilities = this.currentTrack.getCapabilities();
      this.currentDeviceId = capabilities.deviceId as string;
    }

    let facingModeInternal: VideoFacingModeEnum | 'none' = this.facingMode;
    let facingModeExternal: VideoFacingModeEnum | 'none' = 'none';
    const isIOs: boolean = getOSType() === OsType.iOS || getOSType() === OsType.iPad;
    if (!isIOs && this.currentTrack.getSettings) {
      const setting: MediaTrackSettings = this.currentTrack.getSettings();
      if (this.facingMode === 'environment' && setting.facingMode !== 'environment') {
        // 背面カメラを使いたいのに使っていない場合は前面カメラを使っているはずなので設定を書き換える
        facingModeInternal = facingModeExternal = 'user';
      } else if (setting.facingMode === 'user' || setting.facingMode === 'environment') {
        //  前面/背面カメラのどちらを使っているかの確認が取れたので設定する
        facingModeInternal = facingModeExternal = setting.facingMode;
      }
    }
    this.facingModeInternal = facingModeInternal as FacingMode;
    this.$store.commit('camera/setActualFacingMode', facingModeExternal);

    this.video.srcObject = stream;

    // 黒一色確認
    const oneCanvas: HTMLCanvasElement = window.document.createElement('canvas');
    oneCanvas.width = 16;
    oneCanvas.height = 16;
    const oneContext: CanvasRenderingContext2D | null = oneCanvas.getContext('2d');
    if (oneContext !== null) {
      const oneCallback = (): void => {
        let isOneColor: boolean = true;

        oneContext.drawImage(this.video, 0, 0, oneCanvas.width, oneCanvas.height);
        const imageData: ImageData = oneContext.getImageData(0, 0, oneCanvas.width, oneCanvas.height);
        const r0: number = imageData.data[0];
        const g0: number = imageData.data[1];
        const b0: number = imageData.data[2];
        const a0: number = imageData.data[3];
        for (let i = 0; i < imageData.height; i++) {
          for (let j = 0; j < imageData.width; j++) {
            const r1: number = imageData.data[(imageData.width * i + j) * 4 + 0];
            const g1: number = imageData.data[(imageData.width * i + j) * 4 + 1];
            const b1: number = imageData.data[(imageData.width * i + j) * 4 + 2];
            const a1: number = imageData.data[(imageData.width * i + j) * 4 + 3];
            if (r0 !== r1 || g0 !== g1 || b0 !== b1 || a0 !== a1) isOneColor = false;
            if (!isOneColor) break;
          }
          if (!isOneColor) break;
        }

        if (isOneColor) {
          const message: string = `one color r=${r0} g=${g0} b=${b0} a=${a0}) paused=${this.video.paused}`;
          sendLog({ level: 'INFO', kind: 'camera_one_color', message });
        } else {
          this.currentTimerId = window.setTimeout(oneCallback, 1000);
        }
      };
      this.currentTimerId = window.setTimeout(oneCallback, 1000);
    }
  }

  private async onLoadedMetaData() {
    this.initialized(this.video.videoWidth, this.video.videoHeight);
  }

  private async visibilitychangeHandle() {
    const isCurrentTrackReadyStateEnded: boolean = this.currentTrack.readyState === 'ended';

    if (document.visibilityState === 'hidden' && !isCurrentTrackReadyStateEnded) {
      // 非表示になったらカメラの停止
      this.stopCamera();
    } else if (document.visibilityState === 'visible' && isCurrentTrackReadyStateEnded) {
      // 再表示されたらカメラの起動
      await this.startSelectedCamera();
    }
  }

  private stopCamera() {
    window.clearTimeout(this.currentTimerId);
    this.currentDeviceId = '';
    if (!this.currentTrack) {
      return;
    }
    if (this.currentTrack.readyState !== 'ended') {
      this.currentTrack.stop();
    }
  }

  @Emit()
  private initialized(width: number, height: number) {
    const aspect = width / height;
    this.video.width = window.innerWidth;
    this.video.height = window.innerWidth / aspect;
  }

  @Emit()
  private error(err: any) {
    console.error(err); // eslint-disable-line no-console
  }

  @Emit()
  private permissionDenied(err: any) {
    console.error(err); // eslint-disable-line no-console
  }

  @Emit()
  private unsupportedDevice(err: any) {
    console.error(err); // eslint-disable-line no-console
  }

  // 使っていない機能 2021/02/03現在ここは来そうで来ない FaceDirectionTracker.vueで指定しているが、値が変わることがない
  @Watch('idealSize')
  private async onIdealSizeOptionChanged(val: IdealSize, oldVal: IdealSize) {
    const constraints: MediaStreamConstraints = this.createConstraints();
    await this.startCamera(constraints);
  }

  // 使っていない機能 2021/02/03現在ここは来そうで来ない
  //     CameraCapture.vueは使っていない
  //     FaceDirectionTracker.vueで指定しているが、値が変わることがない
  //     CameraBase.vueでは値切り替えの瞬間にアンマウントしているので実質変わらない
  @Watch('facingMode')
  private async onFacingModeChanged(val: FacingMode, oldVal: FacingMode) {
    this.facingModeInternal = val;
    const constraints: MediaStreamConstraints = this.createConstraints();
    await this.startCamera(constraints);
  }

  private get sizeConstraints(): MediaTrackConstraints {
    if (this.idealSize === IdealSize.Max) {
      return {
        // width: { ideal: (capabilities.width as LongRange).max }
        width: { ideal: 1920 } // カメラが落ちる端末があるので、大きすぎない値を設定する
      };
    }
    const sizes = this.idealSize.toString().split('x');
    return {
      width: { ideal: parseInt(sizes[0], 10) }
      // width heightを同時に指定するとエラーになるかも
      // height: { ideal: parseInt(sizes[1], 10) }
    };
  }

  // 使っていない機能
  private applyAdvancedConstraints(key: string, value: boolean | string | number | [Point2D]) {
    const map: { [key: string]: any } = {};
    map[key] = value;
    (this.currentTrack as any)
      .applyConstraints({
        advanced: [map]
      })
      .catch((err: any) => {
        console.error('get errorror', err); // eslint-disable-line no-console
      });
  }

  // 使っていない機能
  private applyAdvancedConstraintsRange(key: string, rate: number) {
    const capabilities = this.currentTrack.getCapabilities() as any;
    const range = capabilities[key] as MediaSettingRange;
    const n = Math.floor(((range.max - range.min) * rate) / range.step) * range.step + range.min;
    this.applyAdvancedConstraints(key, n);
  }
}
